import { useId, useRef, useState, type MouseEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useModalOverlay } from "../components/overlay";
import { Button } from "./Button";
import { IconButton } from "./IconButton";

/**
 * Diálogo e gaveta modais da V2.
 *
 * Todo o comportamento de foco, pilha de `Escape` e bloqueio de rolagem vem de
 * `useModalOverlay`, que continua sendo a fonte única desse contrato
 * (AGENTS §4.2, §14.2). Este componente acrescenta o que falta ali de
 * propósito: a **política de backdrop**.
 *
 * Com `dirty`, o clique fora NÃO fecha: abre a confirmação de descarte exigida
 * por §14.2 e registrada como dívida em §25.7. Sem alteração pendente, o clique
 * fora fecha normalmente.
 *
 * A camada sai por portal no `body`, como no protótipo: dentro da árvore do
 * shell ela ficaria presa ao contexto de empilhamento de `.cx-app` e um menu
 * ancorado no `body` passaria por cima do diálogo.
 */
interface OverlayShellProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Texto de apoio associado por `aria-describedby`. */
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Há alteração não salva: o clique no backdrop pede confirmação. */
  dirty?: boolean;
  /** Foco inicial explícito quando o primeiro focável não é o alvo certo. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
}

interface DialogProps extends OverlayShellProps {
  width?: "default" | "wide";
  /** Selo ao lado do título, como o das janelas de exportação do protótipo. */
  icon?: ReactNode;
}

export function useDiscardPolicy(dirty: boolean, onClose: () => void) {
  const [asking, setAsking] = useState(false);

  const onBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (dirty) {
      setAsking(true);
      return;
    }
    onClose();
  };

  return { asking, setAsking, onBackdrop };
}

export function DiscardConfirm({
  open,
  onKeep,
  onDiscard,
}: {
  open: boolean;
  onKeep: () => void;
  onDiscard: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useModalOverlay({ containerRef: ref, onClose: onKeep, active: open });
  if (!open) return null;

  return createPortal(
    <div className="cx-backdrop" role="presentation">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="cx-dialog"
      >
        <div className="cx-dialog-head">
          <div>
            <p id={titleId} className="cx-dialog-title">
              Descartar alterações?
            </p>
            <p className="cx-dialog-desc">
              O que você digitou ainda não foi salvo e será perdido.
            </p>
          </div>
        </div>
        <div className="cx-dialog-foot">
          <Button variant="ghost" onClick={onKeep}>
            Continuar editando
          </Button>
          <Button variant="danger" onClick={onDiscard}>
            Descartar
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dirty = false,
  initialFocusRef,
  width = "default",
  className,
  icon,
}: DialogProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const { asking, setAsking, onBackdrop } = useDiscardPolicy(dirty, onClose);

  useModalOverlay({
    containerRef,
    onClose,
    initialFocusRef,
    active: open && !asking,
  });

  if (!open) return null;

  return (
    <>
      {createPortal(
      <div className="cx-backdrop" role="presentation" onMouseDown={onBackdrop}>
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          className={className ? `cx-dialog ${className}` : "cx-dialog"}
          data-width={width === "wide" ? "wide" : undefined}
        >
          <div className="cx-dialog-head">
            <div className={icon ? "cx-dialog-titled" : undefined}>
              {icon && (
                <span className="cx-dialog-mark" aria-hidden="true">
                  {icon}
                </span>
              )}
              <div>
                <p id={titleId} className="cx-dialog-title">
                  {title}
                </p>
                {description && (
                  <p id={descId} className="cx-dialog-desc">
                    {description}
                  </p>
                )}
              </div>
            </div>
            <IconButton label="Fechar" icon={<X size={15} />} onClick={onClose} />
          </div>
          {/* Diálogo de confirmação simples só tem descrição e ações: sem corpo,
              não sobra um bloco vazio com espaçamento. */}
          {children != null && children !== false && (
            <div className="cx-dialog-body">{children}</div>
          )}
          {footer && <div className="cx-dialog-foot">{footer}</div>}
        </div>
      </div>,
      document.body,
      )}
      <DiscardConfirm
        open={asking}
        onKeep={() => setAsking(false)}
        onDiscard={() => {
          setAsking(false);
          onClose();
        }}
      />
    </>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  dirty = false,
  initialFocusRef,
  className,
  headerActions,
}: OverlayShellProps & {
  /** Ações ao lado do fechar, como o excluir da edição de tarefa no protótipo. */
  headerActions?: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = useId();
  const { asking, setAsking, onBackdrop } = useDiscardPolicy(dirty, onClose);

  useModalOverlay({
    containerRef,
    onClose,
    initialFocusRef,
    active: open && !asking,
  });

  if (!open) return null;

  return (
    <>
      {createPortal(
      <div
        className="cx-backdrop"
        data-placement="end"
        role="presentation"
        onMouseDown={onBackdrop}
      >
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          className={className ? `cx-drawer ${className}` : "cx-drawer"}
        >
          <div className="cx-drawer-head">
            <div>
              <p id={titleId} className="cx-dialog-title">
                {title}
              </p>
              {description && (
                <p id={descId} className="cx-dialog-desc">
                  {description}
                </p>
              )}
            </div>
            {headerActions ? (
              <div className="cx-drawer-head-actions">
                {headerActions}
                <IconButton label="Fechar" icon={<X size={15} />} onClick={onClose} />
              </div>
            ) : (
              <IconButton label="Fechar" icon={<X size={15} />} onClick={onClose} />
            )}
          </div>
          <div className="cx-drawer-body">{children}</div>
          {footer && <div className="cx-drawer-foot">{footer}</div>}
        </div>
      </div>,
      document.body,
      )}
      <DiscardConfirm
        open={asking}
        onKeep={() => setAsking(false)}
        onDiscard={() => {
          setAsking(false);
          onClose();
        }}
      />
    </>
  );
}
